// Utility functions to reduce code duplication across the application

import { lookupCached } from './propertyCache';
import { extractQuantity } from './quantityParser';

// Convert any of our timestamp shapes to milliseconds since epoch.
// Handles:
//   - live Firestore Timestamp instances (have .toMillis())
//   - JSON-deserialized Timestamps from localStorage: {_seconds, _nanoseconds} or {seconds, nanoseconds}
//   - already-numeric millisecond values
//   - JS Date instances
// Stable across cache → server reconciliation, which is critical because
// stableKey is derived from this. If two derivations of the same item
// disagree on stableKey, React unmounts/remounts the row → visible flicker.
export const toMillis = (timestamp) => {
  if (timestamp == null) return 0;
  if (typeof timestamp === 'number') return timestamp;
  if (typeof timestamp === 'string') {
    const n = Number(timestamp);
    return Number.isFinite(n) ? n : Date.parse(timestamp) || 0;
  }
  if (timestamp instanceof Date) return timestamp.getTime();
  if (typeof timestamp === 'object') {
    if (typeof timestamp.toMillis === 'function') return timestamp.toMillis();
    const sec = timestamp._seconds ?? timestamp.seconds;
    const nano = timestamp._nanoseconds ?? timestamp.nanoseconds ?? 0;
    if (typeof sec === 'number') return sec * 1000 + Math.floor(nano / 1e6);
  }
  return 0;
};

// Compact relative-time label for display on item pills (e.g. "5m", "2h", "yesterday").
export const formatRelativeTime = (timestamp) => {
  const ms = toMillis(timestamp);
  if (!ms || typeof ms !== 'number') return '';
  const diff = Date.now() - ms;
  if (diff < 0) return 'just now';
  const min = Math.floor(diff / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h`;
  const day = Math.floor(hr / 24);
  if (day === 1) return 'yesterday';
  if (day < 7) return `${day}d`;
  if (day < 30) return `${Math.floor(day / 7)}w`;
  return new Date(ms).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

// Generate stable keys for React components
export const generateStableKey = (item) => {
  return `${item.id || item.content}-${toMillis(item.created_at || item.position || Date.now())}`;
};

// Sort key for an item: prefer explicit position, else fall back to created_at
// (handling Firestore Timestamps via toMillis), else 0.
export const getItemSortKey = (item) => {
  if (item.position !== undefined) return item.position;
  const ms = toMillis(item.created_at);
  return typeof ms === 'number' ? ms : 0;
};

// Generate stable list keys
export const generateStableListKey = (list) => {
  return `${list.id}-${toMillis(list.created_at || list.updated_at || Date.now())}`;
};

// Parse multiple items from a single input
export const parseMultipleItems = (content) => {
  // If no separators found, return single item
  if (!content.includes(',') && !content.match(/\sand\s/i)) {
    // console.log(`📝 [Multi-Parse] Single item: "${content}"`);
    return [content.trim()];
  }
  
  // Split on commas first, then handle "and" within each part
  const parts = content.split(',').map(part => part.trim());
  const items = [];
  
  for (const part of parts) {
    // Check if this part contains "and" and isn't a unit measurement
    if (part.match(/\sand\s/i) && !part.match(/\d+\s*(and\s*)?(\d+\/\d+|\.\d+)/i)) {
      // Split on "and" but be careful about "3 and 1/2 cups" style measurements
      const andParts = part.split(/\sand\s/i).map(p => p.trim()).filter(p => p.length > 0);
      items.push(...andParts);
    } else {
      items.push(part);
    }
  }
  
  const filteredItems = items.filter(item => item.length > 0);
  // console.log(`📝 [Multi-Parse] Input: "${content}" -> Found ${filteredItems.length} items:`, filteredItems);
  return filteredItems;
};

// Extract quantity from content. Re-exported from quantityParser to keep the
// existing import path working — the implementation is mirrored with the
// backend so client and server agree on parsing.
export const extractQuantityFromContent = extractQuantity;

// Quick optimistic build for a new item: extracts quantity, looks up known
// static properties from the local cache. Merges in `qty` only if extracted.
export const smartProcessItem = (content, _existingItems, persistentCache = {}) => {
  const quantityInfo = extractQuantityFromContent(content);
  const cached = lookupCached(quantityInfo.baseItem, persistentCache) || {};
  return {
    content: quantityInfo.baseItem,
    quantity: quantityInfo.quantity,
    properties: {
      ...cached,
      ...(quantityInfo.hasQuantity && { qty: quantityInfo.quantity })
    }
  };
};

// Re-export for backward compatibility with existing imports.
export { learnFromItems as updatePersistentCache } from './propertyCache';

// Get local cache from localStorage
export const getLocalCache = () => {
  try {
    const cached = localStorage.getItem('kanto-cache');
    if (!cached) {
      return { lists: [], items: {}, pendingChanges: {}, history: [], propertyCache: {} };
    }
    
    const parsed = JSON.parse(cached);
    
    // Clean history to ensure all items are strings
    const history = parsed.history || [];
    const cleanedHistory = history.filter(item => typeof item === 'string');
    
    if (cleanedHistory.length !== history.length) {
      console.warn(`Cleaned history: removed ${history.length - cleanedHistory.length} non-string items`);
    }
    
    // Ensure all required fields exist for backward compatibility
    return {
      lists: parsed.lists || [],
      items: parsed.items || {},
      pendingChanges: parsed.pendingChanges || {},
      history: cleanedHistory,
      propertyCache: parsed.propertyCache || {} // Persistent cache for AI properties
    };
  } catch (error) {
    console.warn('Failed to parse cached data, using defaults:', error);
    return { lists: [], items: {}, pendingChanges: {}, history: [], propertyCache: {} };
  }
};

// Save local cache to localStorage
export const saveLocalCache = (cache) => {
  try {
    localStorage.setItem('kanto-cache', JSON.stringify(cache));
  } catch (error) {
    console.error('Failed to save persistent cache:', error);
  }
};

// --- Pending change helpers (offline queue) ---

// Add a pending change to the queue
export const addPendingChange = (cache, change) => {
  const id = `pending-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const entry = {
    id,
    ...change,
    createdAt: Date.now(),
    retryCount: 0,
    status: 'pending'
  };
  const pending = { ...(cache.pendingChanges || {}), [id]: entry };
  return { ...cache, pendingChanges: pending };
};

// Queue a single change to be replayed when connectivity returns. Used by hooks
// that need to short-circuit when offline. The optional notifyMessage triggers
// a one-line user-visible toast — pass null to stay silent (e.g., for updates
// where the optimistic UI already conveys the change).
export const queueChangeOffline = (setCache, change, showNotification, notifyMessage = null) => {
  setCache(c => addPendingChange(c, change));
  if (notifyMessage && showNotification) {
    showNotification(notifyMessage, 'info');
  }
};

// Remove a completed or expired pending change
export const removePendingChange = (cache, changeId) => {
  const pending = { ...(cache.pendingChanges || {}) };
  delete pending[changeId];
  return { ...cache, pendingChanges: pending };
};

// Get all pending changes sorted by createdAt (oldest first)
export const getPendingChanges = (cache) => {
  const pending = cache.pendingChanges || {};
  return Object.values(pending).sort((a, b) => a.createdAt - b.createdAt);
};

// Get count of pending changes for badge display
export const getPendingCount = (cache) => {
  return Object.keys(cache.pendingChanges || {}).length;
};

// Add item to history
export const addToHistory = (cache, content) => {
  const newHistory = [...(cache.history || [])];
  
  // Ensure content is a string before processing
  if (typeof content !== 'string') {
    console.warn('Attempted to add non-string content to history:', content);
    return { ...cache, history: newHistory };
  }
  
  const trimmedContent = content.trim();
  
  if (trimmedContent && !newHistory.includes(trimmedContent)) {
    newHistory.unshift(trimmedContent);
    // Keep only last 50 items
    if (newHistory.length > 50) {
      newHistory.splice(50);
    }
  }
  
  return { ...cache, history: newHistory };
};

// Get suggestions from history
export const getSuggestions = (history, input, existingItems = []) => {
  const trimmed = input.trim();
  if (!trimmed) return [];

  const inputLower = trimmed.toLowerCase();

  // From history (deleted items users added before)
  const historyMatches = (history || [])
    .filter((item) => typeof item === 'string' && item.toLowerCase().includes(inputLower));

  // From existing items in the list
  const existingMatches = (existingItems || [])
    .filter((it) => it && typeof it.content === 'string' && it.content.toLowerCase().includes(inputLower))
    .map((it) => it.content);

  // Merge and de-duplicate, prioritize exact prefix matches first
  const merged = Array.from(new Set([...existingMatches, ...historyMatches]));
  merged.sort((a, b) => {
    const aStarts = a.toLowerCase().startsWith(inputLower) ? 0 : 1;
    const bStarts = b.toLowerCase().startsWith(inputLower) ? 0 : 1;
    if (aStarts !== bStarts) return aStarts - bStarts;
    return a.localeCompare(b);
  });

  return merged.slice(0, 7);
};

// Merge lists with server data
export const mergeListsWithServer = (localLists, serverLists) => {
  const merged = [...localLists];
  
  serverLists.forEach(serverList => {
    const existingIndex = merged.findIndex(local => local.id === serverList.id);
    
    if (existingIndex >= 0) {
      // Update existing list with server data
      const local = merged[existingIndex];
      const server = serverList;
      
      // Use server data if it's newer
      const getTime = (list) => {
        return toMillis(list.updated_at || list.created_at || 0);
      };
      
      if (getTime(server) > getTime(local)) {
        merged[existingIndex] = { ...local, ...server };
      }
    } else {
      // Add new server list
      merged.push(serverList);
      
      // Remove any temporary lists that might be duplicates
      // Look for temp lists with the same name created recently
      const tempListIndex = merged.findIndex(local => 
        local.tempId && 
        local.name === serverList.name &&
        Math.abs(toMillis(local.created_at) - toMillis(serverList.created_at)) < 10000 // Within 10 seconds
      );
      
      if (tempListIndex >= 0) {
        // Remove the temporary list since we now have the real one
        console.log(`🔄 [Merge] Removing temp list "${merged[tempListIndex].name}" (${merged[tempListIndex].id}) and replacing with real list "${serverList.name}" (${serverList.id})`);
        merged.splice(tempListIndex, 1);
      }
    }
  });
  
  // Sort by `position` (user-controlled, set on create + on drag-reorder).
  // Older lists may not have `position` yet — fall back to `created_at` so
  // they still get a stable ordering. We deliberately do NOT use
  // `updated_at` here — that changes whenever items inside the list change
  // and would cause lists to "jump" on every edit.
  return merged.sort((a, b) => {
    const getPos = (list) => {
      if (typeof list.position === 'number') return list.position;
      return toMillis(list.created_at || 0);
    };
    return getPos(b) - getPos(a);
  });
};

// Merge items with server data
export const mergeItemsWithServer = (localItems, serverItems) => {
  // Build a quick lookup for server item IDs
  const serverIds = new Set(serverItems.map((it) => it.id));

  // Start from local items but drop any that no longer exist on the server,
  // unless they are clearly optimistic (e.g., temp items awaiting server ack)
  const filteredMerged = (localItems || []).filter((local) => {
    const isOptimisticLocal = Boolean(local?.tempId || local?.isOptimistic || local?.isPendingSync);
    return isOptimisticLocal || serverIds.has(local.id);
  });

  // Merge in server items (update existing entries or add new ones)
  serverItems.forEach((serverItem) => {
    const existingIndex = filteredMerged.findIndex((local) => local.id === serverItem.id);

    if (existingIndex >= 0) {
      const local = filteredMerged[existingIndex];
      const server = serverItem;

      // Prefer newer server data
      const getTimestamp = (item) => toMillis(item.updated_at || item.created_at || item.position || 0);
      if (getTimestamp(server) >= getTimestamp(local)) {
        filteredMerged[existingIndex] = { ...local, ...server };
      }
    } else {
      filteredMerged.push(serverItem);
    }
  });

  // Stable sort by position/created time
  return filteredMerged.sort((a, b) => {
    const getPos = (item) => toMillis(item.position || item.created_at || 0);
    return getPos(a) - getPos(b);
  });
};